import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Kart, Secim, Tablo, TabloBaslik, TabloGovde, Tutar, Uyari } from "@kantin/ui";
import { STOK_YETKILI_ROLLER } from "@kantin/shared";
import type { MalKabulDetayi, MalKabulOzeti } from "@kantin/shared";
import { useYetkili } from "../../veri/oturum.js";
import { tarihSaat } from "../../veri/bicim.js";
import { apiOku, apiYaz } from "./api.js";
import { noktaSecenekleri, useNoktalar } from "./kancalar.js";
import { IslemDurumu, Sayfa, SayfaBasligi, SorguDurumu, miktarGoster } from "./ortak.js";
import { FisFotografi } from "./MalKabulBelgeleri.js";

const DURUM_ADI = { taslak: "Taslak", tamamlandi: "Tamamlandı", iptal: "İptal" };

export function MalKabulGecmisi() {
  const [nokta, setNokta] = useState("");
  const [durum, setDurum] = useState("");
  const [bas, setBas] = useState("");
  const [bit, setBit] = useState("");
  const [firma, setFirma] = useState("");
  const [personel, setPersonel] = useState("");
  const [offset, setOffset] = useState(0);
  const noktalar = useNoktalar();
  const yazabilir = useYetkili(...STOK_YETKILI_ROLLER);
  const sorgu = useQuery({
    queryKey: ["mal-kabul-liste", nokta, durum, bas, bit, firma, personel, offset],
    queryFn: () => apiOku<{ kayitlar: MalKabulOzeti[]; toplam: number }>("/mal-kabul", {
      nokta_id: nokta, durum, bas, bit, firma, personel, offset, limit: 25,
    }),
  });
  const filtrele = (ata: (v: string) => void) => (e: { target: { value: string } }) => { ata(e.target.value); setOffset(0); };
  return <Sayfa>
    <SayfaBasligi baslik="Mal kabul geçmişi" aciklama="Fişleri, ürün satırlarını ve fotoğrafları inceleyin. Tarih aralığı fiş tarihine göre süzülür." />
    {yazabilir && <Link className="underline" to="/stok-giris">Yeni mal kabul</Link>}
    <Kart baslik="Filtreler"><div className="grid gap-3 md:grid-cols-3">
      <Secim etiket="Nokta" value={nokta} onChange={filtrele(setNokta)} secenekler={noktaSecenekleri(noktalar.data?.noktalar)} />
      <Secim etiket="Durum" value={durum} onChange={filtrele(setDurum)} secenekler={[{ deger: "", metin: "Tümü" }, ...Object.entries(DURUM_ADI).map(([deger, metin]) => ({ deger, metin }))]} />
      <Alan etiket="Firma" value={firma} onChange={filtrele(setFirma)} maxLength={200} />
      <Alan etiket="İşlemi yapan" value={personel} onChange={filtrele(setPersonel)} maxLength={200} />
      <Alan etiket="Başlangıç" type="date" value={bas} onChange={filtrele(setBas)} />
      <Alan etiket="Bitiş" type="date" value={bit} onChange={filtrele(setBit)} />
    </div></Kart>
    <Uyari tur="bilgi" baslik="Fişli kayıtlar">Bu liste yeni fiş akışıyla oluşturulan mal kabulleri içerir. Önceki girişler stok hareketlerinde ve denetim kaydında korunur.</Uyari>
    <SorguDurumu yukleniyor={sorgu.isPending} hata={sorgu.error} bos={sorgu.data?.kayitlar.length === 0} bosBaslik="Mal kabul bulunamadı" yenile={() => void sorgu.refetch()}>
      <Tablo><TabloBaslik><th>Fiş</th><th>Fiş tarihi</th><th>Nokta / firma</th><th>İşlemi yapan</th><th>Satır</th><th>Toplam</th><th>Durum</th></TabloBaslik>
        <TabloGovde>{sorgu.data?.kayitlar.map((k) => <tr key={k.id}>
          <td><Link className="underline" to={`/mal-kabul/${k.id}`}>MK-{k.id}</Link></td>
          <td>{k.fis_tarihi}</td><td>{k.nokta_ad}<span className="block text-xs">{k.firma || "—"}</span></td>
          <td>{k.tamamlayan_ad ?? k.olusturan_ad}<span className="block text-xs">{tarihSaat(k.tamamlanma_zamani ?? k.olusma_zamani)}</span></td>
          <td>{k.satir_sayisi}</td><td>{k.toplam_tutar == null ? "Fiyat eksik" : <Tutar deger={k.toplam_tutar} yonsuz />}</td><td>{DURUM_ADI[k.durum]}</td>
        </tr>)}</TabloGovde></Tablo>
    </SorguDurumu>
    <div className="flex items-center gap-3">
      <Buton disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - 25))}>Önceki</Buton>
      <span className="text-sm">{sorgu.data?.toplam ?? 0} kayıt · Sayfa {Math.floor(offset / 25) + 1}</span>
      <Buton disabled={!sorgu.data || offset + 25 >= sorgu.data.toplam} onClick={() => setOffset((o) => o + 25)}>Sonraki</Buton>
    </div>
  </Sayfa>;
}

export function MalKabulDetay() {
  const { id } = useParams();
  const sorgu = useQuery({ queryKey: ["mal-kabul", id], queryFn: () => apiOku<MalKabulDetayi>(`/mal-kabul/${id}`) });
  return <Sayfa>
    <Link className="text-sm underline" to="/mal-kabul">Mal kabul geçmişi</Link>
    <SorguDurumu yukleniyor={sorgu.isPending} hata={sorgu.error} bos={false} yenile={() => void sorgu.refetch()}>
      {sorgu.data && <FisDetayi key={`${id}-${sorgu.data.surum}`} kayit={sorgu.data} />}
    </SorguDurumu>
  </Sayfa>;
}

function FisDetayi({ kayit: k }: { kayit: MalKabulDetayi }) {
  const istemci = useQueryClient();
  const yazabilir = useYetkili(...STOK_YETKILI_ROLLER);
  const [gerekce, setGerekce] = useState("");
  const [iptalAcik, setIptalAcik] = useState(false);
  const islem = useMutation({
    mutationFn: (eylem: "tamamla" | "iptal") => apiYaz<MalKabulDetayi>("POST", `/mal-kabul/${k.id}/${eylem}`, {
      surum: k.surum, ...(eylem === "iptal" ? { gerekce } : {}),
    }),
    onSuccess: (y) => {
      istemci.setQueryData(["mal-kabul", k.id], y);
      void istemci.invalidateQueries({ queryKey: ["mal-kabul-liste"] });
      void istemci.invalidateQueries({ queryKey: ["stok"] });
      void istemci.invalidateQueries({ queryKey: ["audit"] });
    },
  });
  return <>
    <SayfaBasligi baslik={`Mal kabul MK-${k.id}`} aciklama={`${DURUM_ADI[k.durum]} · ${k.nokta_ad}`} />
    <Kart baslik="Fiş özeti"><dl className="grid gap-4 md:grid-cols-3">
      <div><dt>Firma</dt><dd className="m-0 font-medium">{k.firma || "Girilmedi"}</dd></div>
      <div><dt>Fiş tarihi / numarası</dt><dd className="m-0">{k.fis_tarihi} · {k.fis_no || "—"}</dd></div>
      <div><dt>Toplam · {k.satir_sayisi} satır</dt><dd className="m-0">{k.toplam_tutar == null ? "Tüm fiyatlar girilmediği için hesaplanmadı" : <Tutar deger={k.toplam_tutar} yonsuz />}</dd></div>
      <div><dt>Taslağı oluşturan</dt><dd className="m-0">{k.olusturan_ad}<span className="block text-xs">{tarihSaat(k.olusma_zamani)}</span></dd></div>
      {k.tamamlanma_zamani && <div><dt>Tamamlayan</dt><dd className="m-0">{k.tamamlayan_ad}<span className="block text-xs">{tarihSaat(k.tamamlanma_zamani)}</span></dd></div>}
    </dl></Kart>
    <Kart baslik="Ürün satırları"><Tablo><TabloBaslik><th>Ürün</th><th>Miktar</th><th>Birim alış</th><th>Tutar</th><th>SKT / parti</th></TabloBaslik>
      <TabloGovde>{k.satirlar.map((s, i) => <tr key={i}>
        <td>{s.urun_ad}</td><td>{miktarGoster(s.miktar)} {s.birim}</td><td>{s.birim_alis == null ? "Girilmedi" : <Tutar deger={s.birim_alis} yonsuz />}</td>
        <td>{s.tutar == null ? "—" : <Tutar deger={s.tutar} yonsuz />}</td><td>{s.son_kullanma ?? "—"}<span className="block text-xs">{s.parti_kodu ?? "—"}</span></td>
      </tr>)}</TabloGovde></Tablo>{!k.satirlar.length && <p>Henüz ürün satırı yok.</p>}</Kart>
    <Kart baslik="Fiş fotoğrafları"><div className="grid gap-4 md:grid-cols-2">{k.belgeler.map((b) => <FisFotografi key={b.medya_id} belge={b} />)}</div>
      {!k.belgeler.length && <p>Fotoğraf eklenmedi (isteğe bağlı).</p>}
    </Kart>
    {k.durum === "taslak" && <Uyari tur="bilgi" baslik="Henüz stoğa işlenmedi">Satırları ve fiş fotoğraflarını kontrol edin. Tamamladığınızda stok bir kez artar; fiş içeriği sabitlenir.</Uyari>}
    {k.durum === "tamamlandi" && <Uyari tur="onay" baslik="Mal kabul tamamlandı">Bu fişin ürünleri {k.nokta_ad} stoğuna işlendi.</Uyari>}
    {k.durum === "iptal" && <Uyari tur="uyari" baslik="Mal kabul iptal edildi">
      {k.iptal_gerekcesi} · {k.iptal_eden_ad} · {tarihSaat(k.iptal_zamani!)}
      <p>{k.tamamlanma_zamani ? "Girişin stok etkisi ters hareketle geri alındı." : "Taslak iptal edildi; stok hareketi oluşmadı."} Satırlar ve fotoğraflar denetim için korunur.</p>
      {yazabilir && <Link className="underline" to="/stok-giris">Düzeltilmiş bilgilerle yeni mal kabul oluştur</Link>}
    </Uyari>}
    <IslemDurumu hata={islem.error} />
    {yazabilir && k.durum !== "iptal" && <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-4">
        {k.durum === "taslak" && <>
          <Link className="underline" to={`/stok-giris/${k.id}`}>Taslağı düzenle</Link>
          <Buton tur="birincil" disabled={islem.isPending || !k.firma || !k.satirlar.length} onClick={() => islem.mutate("tamamla")}>Mal kabulü tamamla</Buton>
        </>}
        <Buton disabled={islem.isPending} onClick={() => setIptalAcik((o) => !o)}>Gerekçeli iptal</Buton>
      </div>
      {iptalAcik && <Kart baslik="İptal gerekçesi">
        <p>{k.durum === "tamamlandi" ? "Bu fişin tüm miktarları stoktan geri alınır. Düzeltme için ardından yeni fiş oluşturun." : "Taslak iptal edilir. Stok değişmez."}</p>
        <Alan etiket="Gerekçe" value={gerekce} onChange={(e) => setGerekce(e.target.value)} minLength={5} maxLength={500} />
        <Buton disabled={islem.isPending || gerekce.trim().length < 5} onClick={() => islem.mutate("iptal")}>İptali kaydet</Buton>
      </Kart>}
    </div>}
  </>;
}
